export const NAV_SLUGS = [
  "tenders",
  "cases",
  "playbook",
  "question-prompt",
  "plan-prompt",
  "feasibility-prompt",
  "profile-references",
  "cvs",
  "settings"
] as const;

export type NavSlug = (typeof NAV_SLUGS)[number];

export type AppRoute = {
  activeItem: NavSlug;
  selectedCaseId: string | null;
  casePage: "overview" | "questions" | "plan" | "proposal" | "files" | "activity";
  selectedImportFilePath: string | null;
};

const NAV_SLUG_SET = new Set<string>(NAV_SLUGS);

export const DEFAULT_ROUTE: AppRoute = {
  activeItem: "cases",
  selectedCaseId: null,
  casePage: "overview",
  selectedImportFilePath: null
};

const decodePathSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

export const isNavSlug = (value: string): value is NavSlug => NAV_SLUG_SET.has(value);

export const parseAppPath = (pathname: string, search = ""): AppRoute => {
  const segments = pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => decodePathSegment(segment).trim())
    .filter(Boolean);

  if (segments.length === 0) {
    return DEFAULT_ROUTE;
  }

  const [firstSegment, maybeCaseId] = segments;
  if (!isNavSlug(firstSegment)) {
    return DEFAULT_ROUTE;
  }

  if (firstSegment !== "cases") {
    return {
      activeItem: firstSegment,
      selectedCaseId: null,
      casePage: "overview",
      selectedImportFilePath: null
    };
  }

  const rawCasePage = segments[2];
  const casePage =
    rawCasePage === "questions" ||
    rawCasePage === "plan" ||
    rawCasePage === "proposal" ||
    rawCasePage === "files" ||
    rawCasePage === "activity"
      ? rawCasePage
      : "overview";
  const selectedImportFilePath = casePage === "files" ? new URLSearchParams(search).get("path") : null;

  return {
    activeItem: "cases",
    selectedCaseId: maybeCaseId || null,
    casePage,
    selectedImportFilePath
  };
};

export const buildAppPath = (route: AppRoute): string => {
  if (route.activeItem !== "cases") {
    return `/${route.activeItem}`;
  }

  if (!route.selectedCaseId) {
    return "/cases";
  }

  const basePath =
    route.casePage === "overview"
      ? `/cases/${encodeURIComponent(route.selectedCaseId)}`
      : `/cases/${encodeURIComponent(route.selectedCaseId)}/${route.casePage}`;

  if (route.casePage === "files" && route.selectedImportFilePath) {
    const query = new URLSearchParams({ path: route.selectedImportFilePath }).toString();
    return `${basePath}?${query}`;
  }

  return basePath;
};

export const routeForNavSelection = (activeItem: NavSlug): AppRoute => ({
  activeItem,
  selectedCaseId: null,
  casePage: "overview",
  selectedImportFilePath: null
});
